import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Lock,
  Sparkles,
  ArrowRight,
  Store,
} from "lucide-react";
import { LOGO_URL } from "../lib/config";
import { useAuth } from "../context/AuthContext";

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 md:px-12" data-testid="landing-nav">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="DigitalMe" className="h-10 w-10 rounded-lg object-cover" />
          <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
            DigitalMe
          </span>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/login")}
            className="px-5 py-2 text-gray-300 hover:text-cyan-400 font-medium transition"
            data-testid="login-nav-btn"
          >
            Sign In
          </button>
          <button
            onClick={() => navigate("/register")}
            className="px-5 py-2 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-full font-medium hover:shadow-lg hover:shadow-purple-500/30 transition"
            data-testid="register-nav-btn"
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 py-20 md:px-12 md:py-32 text-center" data-testid="hero-section">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-cyan-300 mb-6 border border-purple-500/30">
            <Sparkles className="w-4 h-4" />
            It&apos;s Your Data. Own It.
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Take Control of Your
            <span className="bg-gradient-to-r from-purple-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent"> Personal Data</span>
          </h1>
          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
            Store, protect, and monetize your personal data with AI-powered valuation.
            Your data is valuable — it&apos;s time you benefited from it.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/register")}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 via-violet-600 to-cyan-500 text-white rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-purple-500/40 transition flex items-center justify-center gap-2"
              data-testid="hero-cta-btn"
            >
              Start for Free <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate("/login")}
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition border border-purple-500/30"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-20 md:px-12" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
            Why Choose DigitalMe?
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            A secure, AI-powered platform designed to give you complete control over your personal data
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Lock,
                title: "Encrypted Vault",
                desc: "Your data is encrypted with AES-256 and only accessible by you",
                color: "from-purple-500 to-violet-600",
              },
              {
                icon: Sparkles,
                title: "AI Valuation",
                desc: "Get real-time AI-powered insights on your data's market value",
                color: "from-cyan-500 to-blue-600",
              },
              {
                icon: Shield,
                title: "Privacy First",
                desc: "GDPR & CCPA compliant with full consent management",
                color: "from-violet-500 to-purple-600",
              },
            ].map((f) => (
              <div
                key={f.title}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 hover:shadow-xl hover:shadow-purple-500/10 transition hover:-translate-y-1 border border-purple-500/20"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-5`}>
                  <f.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{f.title}</h3>
                <p className="text-gray-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 py-20 md:px-12 bg-black/30" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Create Account", desc: "Sign up in seconds with secure authentication" },
              { step: "2", title: "Add Your Data", desc: "Upload and categorize your personal data" },
              { step: "3", title: "Get Valuation", desc: "AI analyzes and values your data assets" },
              { step: "4", title: "Take Control", desc: "Decide how and when to monetize your data" },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-cyan-500 text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">
                  {s.step}
                </div>
                <h3 className="font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20 md:px-12" data-testid="cta-section">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-purple-600 via-violet-600 to-cyan-500 rounded-3xl p-12 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Own Your Data?</h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">
            Join thousands of users who have taken control of their personal data
          </p>
          <button
            onClick={() => navigate("/register")}
            className="px-8 py-4 bg-white text-purple-700 rounded-full font-semibold text-lg hover:shadow-xl transition"
          >
            Create Free Account
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-8 md:px-12 border-t border-purple-500/20">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="DigitalMe" className="h-8 w-8 rounded-lg object-cover" />
            <span className="font-semibold text-gray-300">DigitalMe</span>
          </div>
          <p className="text-gray-500 text-sm">© 2025 DigitalMe. Your data, your control.</p>
        </div>
      </footer>
    </div>
  );
};

// Auth Pages
